import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { DxTradeInstrumentDetails } from '@model/DxTrade.types'

export type DxtradeInstrumentMap = Record<string, DxTradeInstrumentDetails | undefined>

const sanitizeSymbols = (symbols?: Array<string | null | undefined>): string[] => {
  if (!symbols?.length) return []
  const sanitized = symbols.filter(
    (symbol): symbol is string => !!symbol && symbol.trim().length > 0
  )
  if (!sanitized.length) return []
  const unique = Array.from(new Set(sanitized.map((symbol) => symbol.trim())))
  unique.sort()
  return unique
}

export const useDxtradeInstruments = (
  accountCode: string,
  symbols?: Array<string | null | undefined>
): {
  instruments: DxtradeInstrumentMap
  isLoading: boolean
  forceRefresh: (skipLoadingFlag?: boolean) => void
} => {
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()

  const normalizedSymbols = useMemo(() => sanitizeSymbols(symbols), [symbols])
  const symbolsKey = useMemo(() => JSON.stringify(normalizedSymbols), [normalizedSymbols])

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.trade.dxtrade.instruments(accountCode, symbolsKey),
    queryFn: async () => {
      const fetchedInstruments = await apiClient.getDxtradeInstruments({
        symbols: normalizedSymbols.join(','),
      })
      const instrumentsBySymbol = fetchedInstruments.reduce<DxtradeInstrumentMap>(
        (acc, instrument) => {
          const symbol = instrument.symbol ?? undefined
          if (symbol) {
            acc[symbol] = instrument
          }
          return acc
        },
        {}
      )

      return Object.fromEntries(
        normalizedSymbols.map((symbol) => [symbol, instrumentsBySymbol[symbol]])
      ) as DxtradeInstrumentMap
    },
    enabled: !!accountCode && normalizedSymbols.length > 0,
    placeholderData: (prev) => prev,
  })

  const forceRefresh = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.trade.dxtrade.instruments(accountCode, symbolsKey),
    })
  }, [queryClient, accountCode, symbolsKey])

  if (!accountCode || normalizedSymbols.length === 0) {
    return {
      instruments: {},
      isLoading: false,
      forceRefresh,
    }
  }

  return {
    instruments: data ?? {},
    isLoading,
    forceRefresh,
  }
}
